import { useMemo, useEffect } from 'react';
import uiUtils from '../../../../Utils/uiUtils';

export const POSITIONING_STRATEGY = {
    AUTO: 'auto',
    DIALOG: 'dialog',
    MODAL: 'modal',
    PORTAL: 'portal',
};

export const POSITIONING_MODE = {
    HIDDEN: 'hidden',
    POPOVER: 'popover',
    FIXED: 'fixed',
    ABSOLUTE: 'absolute',
    PORTAL: 'portal',
};

const calculatePosition = (inputElement) => {
    if (!inputElement) {
        return { top: 0, left: 0, width: 200, openUpwards: false };
    }

    const rect = inputElement.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;

    if (spaceBelow >= 280) {
        return {
            top: rect.bottom + 4,
            left: rect.left,
            width: rect.width,
            openUpwards: false,
        };
    }

    const spaceAbove = rect.top;
    const openUpwards = spaceAbove > spaceBelow;

    return {
        top: openUpwards ? rect.top - 4 : rect.bottom + 4,
        left: rect.left,
        width: rect.width,
        openUpwards,
    };
};

const determineMode = (inputElement, strategy) => {
    switch (strategy) {
        case POSITIONING_STRATEGY.PORTAL:
            return POSITIONING_MODE.PORTAL;

        case POSITIONING_STRATEGY.MODAL:
            return POSITIONING_MODE.ABSOLUTE;

        case POSITIONING_STRATEGY.DIALOG:
            return uiUtils.supportsPopover() ? POSITIONING_MODE.POPOVER : POSITIONING_MODE.FIXED;

        case POSITIONING_STRATEGY.AUTO: {
            const inDialog = uiUtils.isInsideDialog(inputElement);
            if (inDialog) {
                return uiUtils.supportsPopover() ? POSITIONING_MODE.POPOVER : POSITIONING_MODE.FIXED;
            }

            const inReactModal = uiUtils.isInsideReactModal(inputElement);
            if (inReactModal) {
                return POSITIONING_MODE.ABSOLUTE;
            }

            return POSITIONING_MODE.PORTAL;
        }

        default:
            return POSITIONING_MODE.PORTAL;
    }
};

const getFixedStyles = (position) => ({
    position: 'fixed',
    top: `${position.top}px`,
    left: `${position.left}px`,
    width: `${position.width}px`,
    ...(position.openUpwards && {
        transform: 'translateY(-100%)',
        marginTop: '-4px',
    }),
});

const getAbsoluteStyles = () => ({
    position: 'absolute',
    top: '100%',
    left: '0',
    width: '100%',
    marginTop: '4px',
    zIndex: 9999,
});

const getPortalStyles = (position) => ({
    position: 'absolute',
    zIndex: 1,
    top: `${position.top + window.scrollY}px`,
    left: `${position.left + window.scrollX}px`,
    width: `${position.width}px`,
    ...(position.openUpwards && {
        transform: 'translateY(-100%)',
        marginTop: '-4px',
    }),
});

const getPopoverStyles = (position) => ({
    minWidth: `${position.width}px`,
});

const generateStyles = (mode, position) => {
    switch (mode) {
        case POSITIONING_MODE.FIXED:
            return getFixedStyles(position);
        case POSITIONING_MODE.ABSOLUTE:
            return getAbsoluteStyles();
        case POSITIONING_MODE.PORTAL:
            return getPortalStyles(position);
        case POSITIONING_MODE.POPOVER:
            return getPopoverStyles(position);
        default:
            return {};
    }
};

const togglePopover = (dropdown, shouldShow) => {
    if (!dropdown) return;

    try {
        if (shouldShow) {
            dropdown.showPopover();
        } else {
            dropdown.hidePopover();
        }
    } catch (e) {}
};

const applyPopoverPosition = (dropdown, position) => {
    const { top, left, width, openUpwards } = position;

    dropdown.style.top = `${top}px`;
    dropdown.style.left = `${left}px`;
    dropdown.style.width = `${width}px`;

    if (openUpwards) {
        dropdown.style.transform = 'translateY(-100%)';
        dropdown.style.marginTop = '-4px';
    } else {
        dropdown.style.transform = 'none';
        dropdown.style.marginTop = '0';
    }
};

/**
 * usePositioning - Manages dropdown positioning and rendering strategy
 *
 * Rendering strategies:
 * - POPOVER: Native Popover API (for dialogs with browser support)
 * - FIXED: position:fixed (fallback for dialogs without popover support)
 * - ABSOLUTE: position:absolute (for React modals)
 * - PORTAL: Portal to body with position:absolute (default)
 *
 * @param {Object} inputRef - Ref to the input element
 * @param {Object} dropdownRef - Ref to the dropdown element
 * @param {boolean} isOpen - Whether the dropdown should be open
 * @param {string} strategy - Positioning strategy (use POSITIONING_STRATEGY constants)
 * @returns {Object} Positioning configuration
 */
export const usePositioning = (inputRef, dropdownRef, isOpen, strategy = POSITIONING_STRATEGY.AUTO) => {
    const positioning = useMemo(() => {
        if (!isOpen) {
            return { mode: POSITIONING_MODE.HIDDEN, style: {}, usePopover: false, openUpwards: false };
        }

        const inputElement = inputRef.current;
        if (!inputElement) {
            return { mode: POSITIONING_MODE.HIDDEN, style: {}, usePopover: false, openUpwards: false };
        }

        const mode = determineMode(inputElement, strategy);
        const usePopover = mode === POSITIONING_MODE.POPOVER;

        const position = calculatePosition(inputElement);

        const style = generateStyles(usePopover ? POSITIONING_MODE.POPOVER : mode, position);

        return {
            mode,
            style,
            usePopover,
            openUpwards: position.openUpwards,
        };
    }, [inputRef, isOpen, strategy]);

    useEffect(() => {
        const usePopover = positioning.usePopover;

        if (!usePopover) return;

        const dropdown = dropdownRef.current;
        const input = inputRef.current;

        if (!dropdown || !input) return;

        if (isOpen) {
            togglePopover(dropdown, true);
            const position = calculatePosition(input);
            applyPopoverPosition(dropdown, position);
        } else {
            togglePopover(dropdown, false);
        }

        return () => togglePopover(dropdown, false);
    }, [isOpen, positioning.usePopover, dropdownRef, inputRef]);

    return positioning;
};
